import { Anchor, Group, Progress, Stack, Table, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, usePositionNames } from '../../../ui/components';
import { formatDate, formatDateTime, today } from '../../../ui/format';
import { useAssignments, useFleetItems, useInspections, useLastReceived } from '../data';
import { positionsWithoutPerson, vesselOverview } from '../model';
import { PrepLine } from '../widgets';

/** D2 Fleet: one row per vessel — inspection, progress, statuses, waiting for office, gaps. */
export function FleetPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  const { vessels, access, company } = useWorkplace();
  const { byVessel, loading, error } = useFleetItems();
  const inspections = useInspections();
  const assignments = useAssignments(vessels.map((v) => v.id));
  const lastReceived = useLastReceived(vessels.map((v) => v.id));
  const { list } = usePositionNames();
  const order = access.positions.map((p) => p.code);

  return (
    <>
      <PageTitle title={t('fleet.title', { company: company.name })} />
      <QueryState loading={loading || inspections.isPending} error={error ?? inspections.error}>
        {() =>
          vessels.length === 0 ? (
            <Text>{t('fleet.noVessels')}</Text>
          ) : (
            <Table.ScrollContainer minWidth={900}>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('fleet.vessel')}</Table.Th>
                    <Table.Th>{t('fleet.inspection')}</Table.Th>
                    <Table.Th>{t('fleet.progress')}</Table.Th>
                    <Table.Th>{t('fleet.statuses')}</Table.Th>
                    <Table.Th>{t('fleet.waiting')}</Table.Th>
                    <Table.Th>{t('fleet.lastReceived')}</Table.Th>
                    <Table.Th>{t('fleet.noPerson')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {vessels.map((vessel, index) => {
                    const items = byVessel.get(vessel.id) ?? [];
                    const inspection = inspections.data?.get(vessel.id) ?? null;
                    const overview = vesselOverview(items, order, inspection, today());
                    const active = (assignments.data?.get(vessel.id) ?? []).filter(
                      (a) => a.revokedAt === null,
                    );
                    const gaps = positionsWithoutPerson(
                      items,
                      active.map((a) => a.positionCode),
                    );
                    const received = lastReceived[index]?.data ?? null;
                    const { summary } = overview;
                    return (
                      <Table.Tr key={vessel.id}>
                        <Table.Td>
                          <Stack gap={0}>
                            <Anchor component={Link} to={`/vessels/${vessel.id}`} fw={600}>
                              {vessel.name}
                            </Anchor>
                            <Text size="sm" c="dimmed">
                              {t('fleet.imo', { imo: vessel.imo })}
                            </Text>
                          </Stack>
                        </Table.Td>
                        <Table.Td>
                          {inspection === null || overview.prep === null ? (
                            <Text size="sm">{t('inspection.notSet')}</Text>
                          ) : (
                            <Stack gap={0}>
                              <Text size="sm" fw={600}>
                                {formatDate(inspection.plannedOn)}
                              </Text>
                              <PrepLine prep={overview.prep} />
                            </Stack>
                          )}
                        </Table.Td>
                        <Table.Td miw={160}>
                          <Text size="sm">
                            {t('fleet.done', {
                              done: summary.done,
                              total: summary.total,
                              percent: summary.percentDone,
                            })}
                          </Text>
                          <Progress
                            value={summary.percentDone}
                            aria-label={t('fleet.progress')}
                            mt={4}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {t('fleet.statusCounts', {
                              notStarted: summary.byStatus.not_started,
                              inProgress: summary.byStatus.in_progress,
                              closed: summary.byStatus.closed,
                            })}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          {summary.waitingForOffice === 0 ? (
                            <Text size="sm">{t('fleet.none')}</Text>
                          ) : (
                            <Anchor component={Link} to={`/waiting?vessel=${vessel.id}`} fw={600}>
                              {t('fleet.waitingCount', { count: summary.waitingForOffice })}
                            </Anchor>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {received === null ? t('fleet.nothingYet') : formatDateTime(received)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Text size="sm">{list(gaps, t('fleet.none'))}</Text>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )
        }
      </QueryState>
    </>
  );
}
